/**
 * PWS Partner QR Directory — Jun 2026, PODAC Stage 1 (read-only).
 *
 * One row per eligible PWS Partner: Partner ID, name, Region/Territory,
 * canonical referral URL, QR preview, copy link, download PNG/SVG.
 * Also shows which Partners are currently exposed to PODAC and which are
 * held back (missing Region or referral slug) so mismatches are visible.
 */
import { useCallback, useEffect, useState } from "react";
import { Copy, Download, QrCode, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { formatDateAU } from "@/lib/formatDateAU";
import { Button } from "@/components/ui/button";

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function PwsPartnerQrDirectory() {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/podac/pws-qr-directory");
      setData(res.data);
      const rep = await api.get("/admin/podac/attribution-report");
      setReport(rep.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const downloadSvg = async (row) => {
    if (row.qr_svg_data_url) {
      downloadDataUrl(row.qr_svg_data_url, `aware-qr-${row.partner_id}.svg`);
      return;
    }
    try {
      const res = await api.get(
        `/admin/pws-marketing-kit/${row.partner_id}/qr-code`);
      downloadDataUrl(res.data.svg_data_url, `aware-qr-${row.partner_id}.svg`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <PortalShell title="PWS Partner QR Directory">
      <div className="max-w-6xl mx-auto space-y-6" data-testid="pws-qr-directory-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <QrCode className="w-6 h-6 text-cyan-700" /> PWS Partner QR Directory
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Canonical AWARE referral links + QR codes for eligible PWS Partners.
              This exact list is what the PODAC lookup endpoint returns.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="qr-directory-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {data && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900 flex items-center gap-2"
               data-testid="podac-exposure-banner">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            <span>
              <strong>{data.exposed_count}</strong> Partner{data.exposed_count === 1 ? "" : "s"} exposed to PODAC
              via <code className="text-xs bg-white/70 px-1 rounded">{data.podac_endpoint}</code> —
              PODAC key {data.podac_key_configured ? "configured" : <strong className="text-red-700">NOT configured</strong>}.
              Read-only; no Partners are ever created by the lookup.
            </span>
          </div>
        )}

        {loading && <div className="text-sm text-slate-500 py-10 text-center">Loading directory…</div>}

        {!loading && data && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="pws-qr-directory-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3">Partner ID</th>
                  <th className="px-4 py-3">Partner Full Name</th>
                  <th className="px-4 py-3">Region / Territory</th>
                  <th className="px-4 py-3">Referral URL</th>
                  <th className="px-4 py-3">QR</th>
                  <th className="px-4 py-3">Actions</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {data.exposed.map((row) => (
                  <tr key={row.partner_id} className="border-b border-slate-100 align-middle"
                      data-testid={`qr-directory-row-${row.partner_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.partner_id}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{row.partner_full_name}</td>
                    <td className="px-4 py-3 text-slate-700">{row.region}</td>
                    <td className="px-4 py-3">
                      <code className="text-xs text-cyan-800 break-all">{row.referral_url}</code>
                      {row.podac_quote_url && (
                        <div className="mt-1 text-[11px] text-slate-500 break-all" data-testid={`quote-url-${row.partner_id}`}>
                          PODAC: <code>{row.podac_quote_url}</code>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <img src={row.qr_png_data_url} alt={`QR code — ${row.partner_full_name}`}
                           className="w-16 h-16 rounded border border-slate-200"
                           data-testid={`qr-preview-${row.partner_id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5">
                        <Button variant="outline" size="sm" className="h-7 text-xs justify-start"
                                onClick={() => copyLink(row.referral_url)}
                                data-testid={`copy-link-${row.partner_id}`}>
                          <Copy className="w-3 h-3 mr-1" /> Copy link
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs justify-start"
                                onClick={() => downloadDataUrl(row.qr_png_data_url, `aware-qr-${row.partner_id}.png`)}
                                data-testid={`download-png-${row.partner_id}`}>
                          <Download className="w-3 h-3 mr-1" /> PNG
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs justify-start"
                                onClick={() => downloadSvg(row)}
                                data-testid={`download-svg-${row.partner_id}`}>
                          <Download className="w-3 h-3 mr-1" /> SVG
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {row.updated_at ? formatDateAU(row.updated_at) : "—"}
                    </td>
                  </tr>
                ))}
                {data.exposed.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No eligible PWS Partners to expose yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && report && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="attribution-report-panel">
            <div className="font-semibold text-slate-900 text-sm mb-1">
              Partner-attributed bookings by source
            </div>
            <div className="text-xs text-slate-500 mb-3">
              {report.total_partner_attributed} Partner-attributed bookings total ·{" "}
              {report.total_podac_quote} from PODAC quotes
            </div>
            {report.partners.length === 0 ? (
              <div className="text-sm text-slate-500">No Partner-attributed bookings yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Partner</th>
                    <th className="py-2 pr-3">Region</th>
                    <th className="py-2 pr-3">Total</th>
                    <th className="py-2 pr-3">PODAC quote</th>
                    <th className="py-2 pr-3">Other source</th>
                    <th className="py-2">By source/campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {report.partners.map((p) => (
                    <tr key={p.partner_id} className="border-b border-slate-100" data-testid={`attr-report-${p.partner_id}`}>
                      <td className="py-2 pr-3 font-medium text-slate-900">{p.partner_name || p.partner_id}</td>
                      <td className="py-2 pr-3 text-slate-600">{p.region || "—"}</td>
                      <td className="py-2 pr-3">{p.total}</td>
                      <td className="py-2 pr-3 text-cyan-700 font-semibold">{p.podac_quote}</td>
                      <td className="py-2 pr-3">{p.other}</td>
                      <td className="py-2 text-xs text-slate-500">
                        {Object.entries(p.by_source).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && data && data.not_exposed.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="not-exposed-panel">
            <div className="flex items-center gap-2 font-semibold text-amber-900 text-sm mb-2">
              <AlertTriangle className="w-4 h-4" />
              Eligible but NOT exposed to PODAC ({data.not_exposed.length})
            </div>
            <ul className="space-y-1 text-sm text-amber-900">
              {data.not_exposed.map((row) => (
                <li key={row.partner_id} data-testid={`not-exposed-${row.partner_id}`}>
                  <span className="font-medium">{row.partner_full_name || "(no full name)"}</span>
                  <span className="font-mono text-xs text-amber-700"> ({row.partner_id})</span>
                  {" — "}
                  {row.exposure_gaps.map((g) => g.replaceAll("_", " ")).join(", ")}
                </li>
              ))}
            </ul>
            <p className="text-xs text-amber-800 mt-2">
              Fix the Region / referral slug on the Partner record to expose these.
              Partners are never created automatically in this phase.
            </p>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
